/**
 * Demo cohorts: the product, without Classroom and without students.
 *
 * 🔴 EVERYTHING IN ONE IS INVENTED, AND THE PAGE SAYS SO IN THE FIRST SENTENCE. A page that
 * quietly makes people is a page somebody uses on the wrong console; the copy here carries the
 * one fact that matters, and the cohort's own id carries it everywhere else.
 *
 * ⚠️ IT IS NOT A SETTINGS PAGE. Nothing here is configuration: it makes a cohort, fills it,
 * empties it. The Settings group is where it lives because that is where the console's
 * console-wide pages are.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ApiError, copyDemoAssignments, createDemoCohort, deleteDemoCohort, generateDemoStudents,
  getDemoCohorts, type DemoCohort, type DemoList,
} from "../api";
import { routes } from "../routes";
import { Card, CardBody, CardHead, Empty, Pane } from "../ui";
import { useAsync } from "../useAsync";

const DEFAULT_STUDENTS = 20;

export function DemoPage() {
  const q = useAsync(() => getDemoCohorts(), []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); q.reload(); }
    catch (e) { setErr(e as ApiError); }
    finally { setBusy(false); }
  };

  return (
    <div className="stack">
      <Pane q={q} empty={<Empty title="Demo cohorts" />}>
        {(d: DemoList) => (
          <div className="stack">
            <Card>
              <CardHead
                title="Demo cohorts"
                desc={"A cohort this console invented: its students, their answers and its "
                      + "assignments were all made here. Classroom has never heard of it, so "
                      + "it can never write a grade anywhere."} />
              <CardBody>
                {!d.enabled ? (
                  // 🔴 THE REASON, NOT A DISABLED BUTTON. This deployment refuses to make one,
                  // and a greyed control would leave somebody guessing which deployment they
                  // are on.
                  <p className="alert warn stack-tight" role="status">
                    <strong>Not on this deployment.</strong> Demo cohorts hold students nobody
                    enrolled, so they are switched on per deployment with
                    {" "}<code>TC_DEMO_COHORTS</code> — and are off everywhere else, production
                    included.
                  </p>
                ) : (
                  <NewCohort busy={busy} onCreate={(name) =>
                    void act(() => createDemoCohort(name))} />
                )}
                {err && <p className="alert refused" role="alert">⛔ {err.message}</p>}
              </CardBody>
            </Card>

            {d.cohorts.map((c) => (
              <CohortCard key={c.id} cohort={c} sources={d.sources} busy={busy}
                          onGenerate={(n) => void act(() => generateDemoStudents(c.id, n))}
                          onCopy={(src) => void act(() => copyDemoAssignments(c.id, src))}
                          onDelete={() => void act(() => deleteDemoCohort(c.id))} />
            ))}

            {d.enabled && d.cohorts.length === 0 && (
              <p className="small muted">No demo cohort yet. Making one takes a name.</p>
            )}
          </div>
        )}
      </Pane>
    </div>
  );
}

function NewCohort({ busy, onCreate }: { busy: boolean; onCreate: (name: string) => void }) {
  const [name, setName] = useState("");
  return (
    <form className="row gap wrap" style={{ alignItems: "center" }}
          onSubmit={(e) => { e.preventDefault(); onCreate(name); setName(""); }}>
      <label className="q-label" htmlFor="demo-name">Name</label>
      <input id="demo-name" className="input wide" style={{ maxWidth: 320 }} value={name}
             maxLength={256} placeholder="Demo · Q3 26" disabled={busy}
             onChange={(e) => setName(e.target.value)} />
      <button className="btn primary" type="submit" disabled={busy || !name.trim()}>
        New demo cohort
      </button>
    </form>
  );
}

function CohortCard({ cohort, sources, busy, onGenerate, onCopy, onDelete }: {
  cohort: DemoCohort;
  sources: { id: string; name: string }[];
  busy: boolean;
  onGenerate: (count: number) => void;
  onCopy: (sourceCourseId: string) => void;
  onDelete: () => void;
}) {
  const [count, setCount] = useState(DEFAULT_STUDENTS);
  const [source, setSource] = useState(sources[0]?.id ?? "");
  // 🔴 DELETING IS TWO PRESSES. It is the only control here that destroys anything, and the
  // count of what goes is what makes the second press an informed one.
  const [confirming, setConfirming] = useState(false);

  return (
    <Card>
      <CardHead
        title={cohort.name}
        desc={<code>{cohort.id}</code>}
        right={
          <Link className="btn sm" to={routes.teams(cohort.id, "answers")}>Open teams</Link>
        } />
      <CardBody>
        {/* ⚠️ THE COUNTS ARE THE WAY IN, which is the strip this console already uses for a
            page's own facts: every number links to the page that owns it. */}
        <div className="glance">
          <Link to={routes.page(cohort.id, "students")}>
            <span className="k">Students</span><span className="v">{cohort.students}</span>
          </Link>
          <Link to={routes.teams(cohort.id, "answers")}>
            <span className="k">Answers</span><span className="v">{cohort.answers}</span>
          </Link>
          <Link to={routes.page(cohort.id, "assignments")}>
            <span className="k">Assignments</span><span className="v">{cohort.assignments}</span>
          </Link>
          <Link to={routes.teams(cohort.id, "teams")}>
            <span className="k">Plans</span><span className="v">{cohort.proposals}</span>
          </Link>
        </div>

        <div className="row gap wrap" style={{ alignItems: "center", marginTop: 12 }}>
          <label className="q-label" htmlFor={`n-${cohort.id}`}>How many</label>
          <input id={`n-${cohort.id}`} className="input" type="number" min={1} max={120}
                 value={count} disabled={busy}
                 onChange={(e) => setCount(Number(e.target.value))} />
          <button className="btn" disabled={busy} onClick={() => onGenerate(count)}>
            Generate with answers
          </button>
          {/* ⚠️ SAID BEFORE IT HAPPENS. Pressing it twice is how somebody expects to get forty
              students, and it gives twenty — the second press replaces the first lot. */}
          <span className="small muted">Replaces the ones generated before; picks included.</span>
        </div>

        <div className="row gap wrap" style={{ alignItems: "center", marginTop: 10 }}>
          <label className="q-label" htmlFor={`src-${cohort.id}`}>Assignments from</label>
          <select id={`src-${cohort.id}`} className="select" value={source} disabled={busy}
                  onChange={(e) => setSource(e.target.value)}>
            {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button className="btn" disabled={busy || !source} onClick={() => onCopy(source)}>
            Copy briefs
          </button>
          <span className="small muted">
            Titles, briefs and due dates, from what that cohort&rsquo;s Assignments page has
            already listed.
          </span>
        </div>

        <div className="row gap wrap" style={{ alignItems: "center", marginTop: 14 }}>
          <a className="btn sm" href={`/apply/${cohort.id}/project`} target="_blank"
             rel="noreferrer noopener">Open the student page</a>
          <span className="spacer" />
          {confirming ? (
            <>
              <span className="small">
                Delete <b>{cohort.name}</b> and the {cohort.students} invented people in it?
              </span>
              <button className="btn danger sm" disabled={busy}
                      onClick={() => { setConfirming(false); onDelete(); }}>
                Delete it
              </button>
              <button className="btn ghost sm" disabled={busy}
                      onClick={() => setConfirming(false)}>Keep it</button>
            </>
          ) : (
            <button className="btn ghost sm" disabled={busy}
                    onClick={() => setConfirming(true)}>Delete cohort</button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
